import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { STATE_ORDER, oneRowPerSession, tableStateCounts, type TableState } from './tableState';
import type { WaiterTable } from './waiterApi';

export type StateFilterKey = TableState | 'pending';

interface Props {
  /** Already narrowed to the selected salle by the caller (spec §2.2 D2 —
   *  "compteurs = tables de la salle sélectionnée"). */
  tables: WaiterTable[];
  active: StateFilterKey | null;
  onToggle: (state: StateFilterKey) => void;
  onShowAll: () => void;
}

/**
 * Spec 2026-08-28-waiter-salle-ux-design.md §2.2 D2 — the legend/filter row:
 * `Tout n` · `● Libre n` · `● À prendre n` · `● Commandé n` · `● Payé n`,
 * plus a red `● À valider n` only when n > 0. No active chip = show
 * everything, and the leading `Tout` chip (same shape as the Emporter tab's
 * D5 filter) makes that state explicit — tapping it calls `onShowAll`. Tap
 * toggling of the state chips is the caller's responsibility (`onToggle` is
 * called with the tapped key every time, including re-tap — `NotesListPage`
 * decides whether that clears the filter).
 *
 * One line, finger-scrollable (user request 2026-09-01 — with the `Tout` chip
 * the row wrapped onto a second line on 390px, superseding review finding M2's
 * wrap). The scroll affordance is explicit: a `‹`/`›` chevron on a paper fade
 * at whichever edge still hides chips, not just a cut-off chip.
 */
export default function TableStateFilter({ tables, active, onToggle, onShowAll }: Props) {
  const { t } = useTranslation();
  const counts = tableStateCounts(tables);

  const scrollerRef = useRef<HTMLDivElement>(null);
  const [moreLeft, setMoreLeft] = useState(false);
  const [moreRight, setMoreRight] = useState(false);
  const updateHints = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setMoreLeft(el.scrollLeft > 4);
    setMoreRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  // Chip widths move with the counts, so re-measure after every render (the
  // setStates above no-op when nothing changed) plus on viewport resize.
  useEffect(updateHints);
  useEffect(() => {
    window.addEventListener('resize', updateHints);
    // jsdom has no ResizeObserver (src/test/setup.ts polyfills others only).
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateHints) : null;
    if (scrollerRef.current) ro?.observe(scrollerRef.current);
    return () => {
      window.removeEventListener('resize', updateHints);
      ro?.disconnect();
    };
  }, []);

  return (
    <div data-testid="state-filter-rail" className="relative bg-paper border-b border-hairline">
      <div
        ref={scrollerRef}
        onScroll={updateHints}
        data-testid="state-filter"
        role="group"
        aria-label={t('waiter.notes.title')}
        className="waiter-hscroll flex items-center gap-1.5 px-3 py-2"
      >
        <button
          type="button"
          data-testid="state-chip-all"
          aria-pressed={active === null}
          onClick={onShowAll}
          // Same fill-when-active shape as `EmporterSourceFilter`'s `Tout` —
          // no state dot, this chip is "no filter", not a fifth state.
          className={`shrink-0 h-11 px-2 flex items-center gap-1 rounded-full border-2 font-sans text-[14px] font-semibold ${
            active === null ? 'border-ink bg-ink text-paper' : 'border-transparent text-ink'
          }`}
        >
          {t('waiter.notes.states.all')} {oneRowPerSession(tables).length}
        </button>
        {STATE_ORDER.map((state) => (
          <button
            key={state}
            type="button"
            data-testid={`state-chip-${state}`}
            aria-pressed={active === state}
            onClick={() => onToggle(state)}
            // The dot's colour rides `.w-chip-${state}`'s own `--w-chip-dot`
            // custom property (waiter-root.css) — the label stays `text-ink`
            // so it clears 4.5:1 on `--paper` (review F1F2#5; the semantic
            // ring colours are all below that threshold as text).
            className={`w-chip-${state} shrink-0 h-11 px-2 flex items-center gap-1 rounded-full border-2 font-sans text-[14px] font-semibold text-ink ${
              active === state ? 'border-current' : 'border-transparent'
            }`}
          >
            {t(`waiter.notes.states.${state}`)} {counts[state]}
          </button>
        ))}
        {counts.pending > 0 && (
          <button
            type="button"
            data-testid="state-chip-pending"
            aria-pressed={active === 'pending'}
            onClick={() => onToggle('pending')}
            style={{ color: 'var(--w-danger)' }}
            className={`shrink-0 h-11 px-2 flex items-center gap-1 rounded-full border-2 font-sans text-[14px] font-bold ${
              active === 'pending' ? 'border-current' : 'border-transparent'
            }`}
          >
            {t('waiter.notes.states.pending')} {counts.pending}
          </button>
        )}
      </div>
      {moreLeft && (
        <div
          data-testid="state-filter-more-left"
          aria-hidden="true"
          className="absolute inset-y-0 left-0 w-10 flex items-center pl-1 pointer-events-none"
          style={{ background: 'linear-gradient(to right, var(--paper) 35%, transparent)' }}
        >
          <span className="text-xl font-bold text-ink leading-none">‹</span>
        </div>
      )}
      {moreRight && (
        <div
          data-testid="state-filter-more-right"
          aria-hidden="true"
          className="absolute inset-y-0 right-0 w-10 flex items-center justify-end pr-1 pointer-events-none"
          style={{ background: 'linear-gradient(to left, var(--paper) 35%, transparent)' }}
        >
          <span className="text-xl font-bold text-ink leading-none">›</span>
        </div>
      )}
    </div>
  );
}
